import styles from './OpenSeadragon.module.css'
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import OpenSeadragon from 'openseadragon';
import api from '../API';
import { extractData } from '../utils/apiResponse';
import {
    createPathPresenterTileSource,
    pathPresenterMetaFromItem,
} from './PathPresenterTileSource';

class CustomTileSource extends OpenSeadragon.TileSource {
    constructor(baseUrl, metadata) {
        super({
            width: metadata.sizeX,
            height: metadata.sizeY,
            tileWidth: metadata.tileWidth,
            tileHeight: metadata.tileHeight,
            minLevel: 0,
            maxLevel: metadata.levels-1
        });
        this.baseUrl = baseUrl;

        // OPA / scalebar contract: source.mpp = { x, y } in µm per source pixel.
        const mmX = Number(metadata?.mm_x);
        const mmY = Number(metadata?.mm_y);
        if (Number.isFinite(mmX) && mmX > 0) {
            const mmYSafe = Number.isFinite(mmY) && mmY > 0 ? mmY : mmX;
            this.mpp = { x: mmX * 1000, y: mmYSafe * 1000 };
        }
    }

    getTileUrl(level, x, y) {
        return `${this.baseUrl}/${level}/${x}/${y}`;
    }

    getTileAjaxHeaders() {
        return {
            'Girder-Token': localStorage.girderToken
        };
    }
}

/**
 * WSI viewer: accepts either pre-fetched tile data or an item id to fetch tiles.
 * - girderItemId + tileMetadata: use pre-fetched data (e.g. from unified BDSA item endpoint), no fetch.
 * - data: legacy item object with _id; uses data as tile source if it has sizeX/levels, else fetches item/:id/tiles.
 * - itemId: fetch tile data via GET item/:itemId/tiles.
 * - onViewerReady: optional (viewer) => void, called with the OSD instance for composition (e.g. annotation toolkit).
 * Sets window.viewer when the viewer is created; cleared on unmount for console debugging.
 *
 * Attaches ConfigurationWidget + MeasurementsOverlay (scalebar default-on, panel closed)
 * for all viewer contexts.
 */
function OpenSeadragonViewer({ data, girderItemId, tileMetadata, itemId, style, onViewerReady }) {
    const id = girderItemId || itemId || (data && data._id) || null;
    const inlinePp = pathPresenterMetaFromItem(data);

    const { data: itemResponse, isFetched: itemFetched } = useQuery({
        queryKey: ['girder-item', id],
        queryFn: () => api.get(`item/${id}`),
        staleTime: 5 * 60 * 1000,
        enabled: !!id && !inlinePp,
    });
    const itemDoc = extractData(itemResponse);
    const pp = inlinePp || pathPresenterMetaFromItem(itemDoc);
    const isPathPresenter = !!pp;
    const itemReady = !!inlinePp || !id || itemFetched;

    const hasPreFetched = !!(girderItemId && tileMetadata && tileMetadata.sizeX != null && tileMetadata.levels != null);
    const dataHasTiles = !!(data && data.sizeX != null && data.levels != null);

    const { data: tilesResponse } = useQuery({
        queryKey: ['item-tiles', id],
        queryFn: () => api.get(`item/${id}/tiles`),
        staleTime: 5 * 60 * 1000,
        enabled: !!id && itemReady && !isPathPresenter && !hasPreFetched && !dataHasTiles,
    });

    const tileData = isPathPresenter
        ? null
        : (hasPreFetched ? tileMetadata : (extractData(tilesResponse) || (dataHasTiles ? data : null)));

    useEffect(() => {
        if (!id) return;
        if (!itemReady) return;
        if (!isPathPresenter && !tileData) return;

        let cancelled = false;
        let measurements = null;
        let configWidget = null;
        let viewer = null;
        let onTileFailed = null;
        const prevOSD = window.OpenSeadragon;
        window.OpenSeadragon = OpenSeadragon;

        (async () => {
            let source;
            try {
                source = isPathPresenter
                    ? await createPathPresenterTileSource(id, pp)
                    : new CustomTileSource(api.url(`item/${id}/tiles/zxy`), tileData);
            } catch (err) {
                console.error('OpenSeadragonViewer: failed to create tile source', err);
                return;
            }
            if (cancelled) return;

            viewer = OpenSeadragon({
                id: 'seadragon-viewer-' + id,
                prefixUrl: '//openseadragon.github.io/openseadragon/images/',
                tileSources: source
            });
            window.viewer = viewer;

            if (isPathPresenter && source.refreshSasIfNeeded) {
                onTileFailed = () => {
                    source.refreshSasIfNeeded(true).catch((err) => {
                        console.error('OpenSeadragonViewer: SAS refresh failed', err);
                    });
                };
                viewer.addHandler('tile-load-failed', onTileFailed);
            }

            try {
                const mod = await import(
                    /* webpackChunkName: "osd-annotation" */ '../submodules/osd-paperjs-annotation/dist/main.js'
                );
                if (cancelled) return;
                const ConfigurationWidget =
                    mod.ConfigurationWidget
                    || mod.default?.ConfigurationWidget
                    || (typeof window !== 'undefined' && window.ConfigurationWidget);
                const MeasurementsOverlay =
                    mod.MeasurementsOverlay
                    || mod.default?.MeasurementsOverlay
                    || (typeof window !== 'undefined' && window.MeasurementsOverlay);
                if (!ConfigurationWidget || !MeasurementsOverlay) {
                    throw new Error('ConfigurationWidget or MeasurementsOverlay missing from osd-paperjs-annotation dist');
                }
                configWidget = new ConfigurationWidget(viewer, {
                    storageKey: 'bdsa-viewer-overlays',
                });
                // Unified measurements: scalebar visible by default; panel closed until toolbar toggle.
                measurements = new MeasurementsOverlay(viewer, { scalebarStartIn: 'showing' });
                if (cancelled) {
                    try { measurements.destroy(); } catch { /* ignore */ }
                    try { configWidget.destroy(); } catch { /* ignore */ }
                    measurements = null;
                    configWidget = null;
                    return;
                }
                window.measurementsOverlay = measurements;
            } catch (err) {
                console.error('OpenSeadragonViewer: failed to attach measurements overlay', err);
            } finally {
                if (!cancelled) {
                    onViewerReady?.(viewer);
                }
            }
        })();

        return () => {
            cancelled = true;
            if (viewer && onTileFailed) {
                try { viewer.removeHandler('tile-load-failed', onTileFailed); } catch { /* ignore */ }
            }
            if (measurements) {
                try {
                    measurements.destroy();
                } catch (e) {
                    console.warn('OpenSeadragonViewer: measurementsOverlay.destroy error', e);
                }
            }
            if (configWidget) {
                try {
                    configWidget.destroy();
                } catch (e) {
                    console.warn('OpenSeadragonViewer: configurationWidget.destroy error', e);
                }
            }
            if (window.measurementsOverlay === measurements) {
                window.measurementsOverlay = null;
            }
            window.viewer = null;
            if (window.OpenSeadragon === OpenSeadragon) {
                if (prevOSD !== undefined) {
                    window.OpenSeadragon = prevOSD;
                } else {
                    delete window.OpenSeadragon;
                }
            }
            if (viewer) {
                viewer.destroy();
            }
        };
    }, [tileData, id, isPathPresenter, itemReady, pp]);

    if (!id) return null;

    return (
        <div id={'seadragon-viewer-' + id} className={styles.viewer} style={style} />
    );
}

export default OpenSeadragonViewer;
